<script setup>
/* global defineProps */
import { computed, nextTick, onBeforeUnmount, ref } from 'vue'

const props = defineProps({
  apiBaseUrl: {
    type: String,
    default: ''
  }
})

const isOpen = ref(false)
const input = ref('')
const loading = ref(false)
const messageArea = ref(null)
const inputArea = ref(null)
const requestController = ref(null)

const messages = ref([
  {
    role: 'assistant',
    content: '안녕하세요. 위케어 암 정보와 자주 묻는 질문을 찾아드리고, 일상적인 대화도 나눌 수 있어요.',
    sources: [],
    media: []
  }
])

const suggestions = [
  '암 환자의 식사 관리 방법을 알려주세요',
  '위암 수술 후 식사는 어떻게 해야 하나요?',
  '안녕하세요! 오늘 기분은 어때요?'
]

const CHAT_HISTORY_LIMIT = 6

const canSend = computed(() => input.value.trim().length > 0 && !loading.value)
const chatApiUrl = computed(() => {
  const baseUrl = props.apiBaseUrl.trim().replace(/\/$/, '')
  return `${baseUrl}/api/chat`
})

async function scrollToBottom() {
  await nextTick()
  if (messageArea.value) {
    messageArea.value.scrollTop = messageArea.value.scrollHeight
  }
}

async function toggleChat() {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    await nextTick()
    inputArea.value?.focus()
    await scrollToBottom()
  }
}

function closeChat() {
  isOpen.value = false
}

async function sendMessage(text = input.value.trim()) {
  const question = text.trim()
  if (!question || loading.value) return

  const history = messages.value
    .filter((message) => !message.error && ['user', 'assistant'].includes(message.role))
    .slice(-CHAT_HISTORY_LIMIT)
    .map(({ role, content }) => ({ role, content }))
  messages.value.push({ role: 'user', content: question })
  input.value = ''
  loading.value = true
  await scrollToBottom()

  const controller = new AbortController()
  requestController.value = controller
  const timeoutId = window.setTimeout(() => controller.abort(), 45000)

  try {
    const response = await fetch(chatApiUrl.value, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question, history }),
      signal: controller.signal
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      throw new Error(data.message || '챗봇 서버에서 응답을 받지 못했습니다.')
    }
    messages.value.push({
      role: 'assistant',
      content: data.content || '답변을 생성하지 못했습니다.',
      sources: data.sources || [],
      media: data.media || []
    })
  } catch (error) {
    const message = error.name === 'AbortError'
      ? '응답 시간이 초과되었습니다. 잠시 후 다시 시도해주세요.'
      : (error.message || '챗봇 연결 중 오류가 발생했습니다.')
    messages.value.push({ role: 'assistant', content: message, error: true })
  } finally {
    window.clearTimeout(timeoutId)
    requestController.value = null
    loading.value = false
    await scrollToBottom()
    inputArea.value?.focus()
  }
}

function youtubeEmbedUrl(url) {
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]+)/)
  return match ? `https://www.youtube.com/embed/${match[1]}` : ''
}

function internalSourceUrl(url) {
  try {
    const parsedUrl = new URL(url, window.location.href)
    // 수집 당시 호스트 대신 현재 user-web의 호스트를 유지한다.
    return parsedUrl.hash || `${parsedUrl.pathname}${parsedUrl.search}`
  } catch {
    return url
  }
}

onBeforeUnmount(() => requestController.value?.abort())
</script>

<template>
  <div class="wecare-chatbot">
    <Transition name="wecare-chatbot-pop">
      <section
        v-if="isOpen"
        class="wecare-chatbot-panel"
        role="dialog"
        aria-label="위케어 정보 안내 챗봇"
        @keydown.esc="closeChat"
      >
        <header class="wecare-chatbot-header">
          <span class="wecare-chatbot-ai" aria-hidden="true">AI</span>
          <div class="wecare-chatbot-heading">
            <strong>정보 안내 챗봇</strong>
            <small><i aria-hidden="true"></i> 위케어 정보와 일상대화를 제공합니다</small>
          </div>
          <button type="button" class="wecare-chatbot-close" aria-label="챗봇 닫기" @click="closeChat">×</button>
        </header>

        <div ref="messageArea" class="wecare-chatbot-messages" aria-live="polite">
          <article
            v-for="(message, index) in messages"
            :key="index"
            :class="['wecare-chatbot-message', `is-${message.role}`]"
          >
            <div :class="['wecare-chatbot-bubble', { 'is-error': message.error }]">
              {{ message.content }}
            </div>

            <div v-if="message.media?.length" class="wecare-chatbot-media">
              <template v-for="item in message.media" :key="item.url">
                <img v-if="item.type === 'image'" :src="item.url" :alt="item.title || '관련 이미지'">
                <iframe
                  v-else-if="item.type === 'youtube' && youtubeEmbedUrl(item.url)"
                  :src="youtubeEmbedUrl(item.url)"
                  :title="item.title || '관련 영상'"
                  loading="lazy"
                  allowfullscreen
                ></iframe>
              </template>
            </div>

            <div v-if="message.sources?.length" class="wecare-chatbot-sources">
              <strong>관련 자료</strong>
              <a
                v-for="source in message.sources"
                :key="source.url"
                :href="internalSourceUrl(source.url)"
              >{{ source.title }} <span aria-hidden="true">→</span></a>
            </div>
          </article>

          <div v-if="loading" class="wecare-chatbot-message is-assistant">
            <div class="wecare-chatbot-bubble wecare-chatbot-loading">
              <span></span><span></span><span></span>
              <em>답변을 찾고 있습니다</em>
            </div>
          </div>
        </div>

        <div v-if="messages.length === 1" class="wecare-chatbot-suggestions">
          <button v-for="item in suggestions" :key="item" type="button" @click="sendMessage(item)">
            {{ item }}
          </button>
        </div>

        <form class="wecare-chatbot-form" @submit.prevent="sendMessage()">
          <textarea
            ref="inputArea"
            v-model="input"
            maxlength="500"
            rows="1"
            aria-label="챗봇 질문"
            placeholder="궁금한 내용을 입력하세요"
            @keydown.enter.exact.prevent="sendMessage()"
          ></textarea>
          <button type="submit" :disabled="!canSend" aria-label="질문 보내기">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M3.4 20.4 21 12 3.4 3.6l-.1 6.5L15.9 12 3.3 13.9z" />
            </svg>
          </button>
        </form>
        <footer>AI 답변은 참고용이며 의료진의 진료를 대신하지 않습니다.</footer>
      </section>
    </Transition>

    <button
      type="button"
      class="wecare-chatbot-launcher"
      :class="{ 'is-open': isOpen }"
      :aria-label="isOpen ? '챗봇 닫기' : '챗봇 열기'"
      :aria-expanded="isOpen"
      @click="toggleChat"
    >
      <span v-if="isOpen" aria-hidden="true">×</span>
      <svg v-else viewBox="0 0 28 28" aria-hidden="true">
        <path d="M5.2 4.5h17.6A3.2 3.2 0 0 1 26 7.7v10.5a3.2 3.2 0 0 1-3.2 3.2H12.2l-6.6 4v-4H5.2A3.2 3.2 0 0 1 2 18.2V7.7a3.2 3.2 0 0 1 3.2-3.2Z" />
        <circle cx="8.5" cy="13" r="1.5" />
        <circle cx="14" cy="13" r="1.5" />
        <circle cx="19.5" cy="13" r="1.5" />
      </svg>
    </button>
  </div>
</template>

<style scoped>
.wecare-chatbot {
  --chat-primary: #16897a;
  --chat-primary-dark: #0d6f63;
  --chat-text: #263c3a;
  --chat-muted: #70827f;
  font-family: 'noto-sans-kr', sans-serif;
}

.wecare-chatbot-launcher {
  position: fixed;
  right: 28px;
  bottom: 28px;
  z-index: 10001;
  display: grid;
  place-items: center;
  width: 62px;
  height: 62px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: #fff;
  background: var(--chat-primary);
  box-shadow: 0 12px 30px rgba(13, 111, 99, 0.35);
  transition: transform 0.18s ease, background 0.18s ease;
}

.wecare-chatbot-launcher:hover {
  background: var(--chat-primary-dark);
  transform: translateY(-2px);
}

.wecare-chatbot-launcher:focus-visible,
.wecare-chatbot-panel button:focus-visible,
.wecare-chatbot-panel a:focus-visible,
.wecare-chatbot-panel textarea:focus-visible {
  outline: 3px solid #f5c42b;
  outline-offset: 2px;
}

.wecare-chatbot-launcher svg {
  width: 29px;
  height: 29px;
  fill: #fff;
}

.wecare-chatbot-launcher > span {
  font-size: 34px;
  font-weight: 300;
  line-height: 1;
}

.wecare-chatbot-panel {
  position: fixed;
  right: 28px;
  bottom: 104px;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  width: min(400px, calc(100vw - 32px));
  height: min(680px, calc(100vh - 132px));
  overflow: hidden;
  border: 1px solid rgba(22, 137, 122, 0.12);
  border-radius: 24px;
  color: var(--chat-text);
  background: #fff;
  box-shadow: 0 24px 70px rgba(26, 62, 57, 0.22);
}

.wecare-chatbot-header {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 12px;
  min-height: 78px;
  padding: 16px 18px;
  color: #fff;
  background: linear-gradient(135deg, var(--chat-primary), var(--chat-primary-dark));
}

.wecare-chatbot-ai {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 800;
  background: rgba(255, 255, 255, 0.18);
}

.wecare-chatbot-heading {
  min-width: 0;
}

.wecare-chatbot-heading strong,
.wecare-chatbot-heading small {
  display: block;
}

.wecare-chatbot-heading strong {
  font-size: 16px;
  line-height: 1.4;
}

.wecare-chatbot-heading small {
  margin-top: 4px;
  font-size: 11px;
  opacity: 0.9;
}

.wecare-chatbot-heading i {
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-right: 4px;
  border-radius: 50%;
  background: #8bf1bd;
  vertical-align: 1px;
}

.wecare-chatbot-close {
  width: 38px;
  height: 38px;
  margin-left: auto;
  padding: 0;
  border: 0;
  color: #fff;
  font-size: 28px;
  font-weight: 300;
  line-height: 1;
  background: transparent;
}

.wecare-chatbot-messages {
  flex: 1 1 auto;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 20px;
  background: #f5f9f8;
  scroll-behavior: smooth;
}

.wecare-chatbot-message {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 16px;
}

.wecare-chatbot-message.is-user {
  align-items: flex-end;
}

.wecare-chatbot-bubble {
  max-width: 88%;
  padding: 12px 15px;
  border: 1px solid #e8efed;
  border-radius: 7px 18px 18px 18px;
  font-size: 14px;
  line-height: 1.65;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  background: #fff;
  box-shadow: 0 3px 12px rgba(33, 73, 67, 0.05);
}

.is-user .wecare-chatbot-bubble {
  border-color: var(--chat-primary);
  border-radius: 18px 18px 7px 18px;
  color: #fff;
  background: var(--chat-primary);
}

.wecare-chatbot-bubble.is-error {
  border-color: #f0cece;
  color: #a33c3c;
  background: #fff8f8;
}

.wecare-chatbot-media,
.wecare-chatbot-sources {
  width: 88%;
  margin-top: 8px;
}

.wecare-chatbot-media img,
.wecare-chatbot-media iframe {
  display: block;
  width: 100%;
  margin-top: 8px;
  border: 0;
  border-radius: 12px;
  background: #e8efed;
}

.wecare-chatbot-media img {
  max-height: 190px;
  object-fit: cover;
}

.wecare-chatbot-media iframe {
  aspect-ratio: 16 / 9;
}

.wecare-chatbot-sources > strong {
  display: block;
  margin-bottom: 6px;
  color: var(--chat-muted);
  font-size: 11px;
}

.wecare-chatbot-sources a {
  display: block;
  margin-top: 5px;
  padding: 9px 11px;
  border: 1px solid #d5e5e1;
  border-radius: 10px;
  color: var(--chat-primary-dark);
  font-size: 12px;
  line-height: 1.4;
  text-decoration: none;
  background: #fff;
}

.wecare-chatbot-sources a:hover {
  border-color: var(--chat-primary);
  background: #f6fffc;
}

.wecare-chatbot-loading {
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--chat-muted);
}

.wecare-chatbot-loading span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--chat-primary);
  animation: wecare-chatbot-dot 1.1s infinite ease-in-out;
}

.wecare-chatbot-loading span:nth-child(2) { animation-delay: 0.14s; }
.wecare-chatbot-loading span:nth-child(3) { animation-delay: 0.28s; }

.wecare-chatbot-loading em {
  margin-left: 5px;
  font-size: 12px;
  font-style: normal;
}

.wecare-chatbot-suggestions {
  display: flex;
  flex: 0 0 auto;
  gap: 7px;
  overflow-x: auto;
  padding: 10px 16px 4px;
  background: #fff;
  scrollbar-width: none;
}

.wecare-chatbot-suggestions::-webkit-scrollbar {
  display: none;
}

.wecare-chatbot-suggestions button {
  flex: 0 0 auto;
  padding: 8px 11px;
  border: 1px solid #b7d9d3;
  border-radius: 18px;
  color: var(--chat-primary-dark);
  font-size: 12px;
  white-space: nowrap;
  background: #fff;
}

.wecare-chatbot-suggestions button:hover {
  background: #eef9f6;
}

.wecare-chatbot-form {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 10px;
  padding: 13px 14px;
  border-top: 1px solid #e7edeb;
  background: #fff;
}

.wecare-chatbot-form textarea {
  flex: 1 1 auto;
  min-width: 0;
  height: 42px;
  padding: 10px 12px;
  resize: none;
  border: 1px solid #dce7e4;
  border-radius: 12px;
  color: var(--chat-text);
  font-size: 14px;
  line-height: 20px;
  outline: 0;
  background: #f8faf9;
}

.wecare-chatbot-form textarea::placeholder {
  color: #99a5a3;
}

.wecare-chatbot-form > button {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 42px;
  height: 42px;
  padding: 0;
  border: 0;
  border-radius: 12px;
  background: var(--chat-primary);
}

.wecare-chatbot-form > button:disabled {
  cursor: default;
  opacity: 0.35;
}

.wecare-chatbot-form svg {
  width: 20px;
  height: 20px;
  fill: #fff;
}

.wecare-chatbot-panel > footer {
  flex: 0 0 auto;
  padding: 0 12px 10px;
  color: #8c9997;
  font-size: 10px;
  line-height: 1.4;
  text-align: center;
  background: #fff;
}

.wecare-chatbot-pop-enter-active,
.wecare-chatbot-pop-leave-active {
  transform-origin: right bottom;
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.wecare-chatbot-pop-enter-from,
.wecare-chatbot-pop-leave-to {
  opacity: 0;
  transform: translateY(8px) scale(0.96);
}

@keyframes wecare-chatbot-dot {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.35; }
  30% { transform: translateY(-3px); opacity: 1; }
}

@media (max-width: 520px) {
  .wecare-chatbot-launcher {
    right: 16px;
    bottom: 16px;
    width: 56px;
    height: 56px;
  }

  .wecare-chatbot-panel {
    right: 8px;
    bottom: 82px;
    width: calc(100vw - 16px);
    height: calc(100dvh - 98px);
    max-height: none;
    border-radius: 20px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .wecare-chatbot-pop-enter-active,
  .wecare-chatbot-pop-leave-active,
  .wecare-chatbot-launcher {
    transition: none;
  }

  .wecare-chatbot-loading span {
    animation: none;
  }
}
</style>
